import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Role } from '../../types';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { useRolePermissions } from './useRolePermissions';
import ContentTypesTab from './ContentTypesTab';
import PluginsTab from './PluginsTab';

interface RoleFormProps {
  role?: Role;
  onSubmit: (data: Partial<Role>) => void;
  onCancel: () => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ role, onSubmit, onCancel }) => {
  const [activeTab, setActiveTab] = useState<'content' | 'plugins'>('content');
  const [expandedCt, setExpandedCt] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<Role>({
    defaultValues: role || { name: '', description: '', isActive: true }
  });

  useEffect(() => {
    if (role) reset(role);
  }, [role, reset]);

  const perms = useRolePermissions(role);

  const onFormSubmit = (data: Role) => {
    onSubmit({
      name: data.name,
      description: data.description,
      permissions: perms.buildPermissions() as any,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {role ? `Edit Role: ${role.name}` : 'Create New Role'}
          </h2>
          {role?.isSystem && (
            <p className="text-sm text-yellow-600 mt-1">
              This is a system role. Permissions cannot be modified.
            </p>
          )}
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Role Name *"
                {...register('name', { required: 'Role name is required' })}
                error={errors.name?.message}
                placeholder="e.g., Content Manager"
                disabled={role?.isSystem}
              />
              <TextArea
                label="Description"
                {...register('description')}
                error={errors.description?.message}
                placeholder="Brief description of this role"
                rows={2}
                disabled={role?.isSystem}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-gray-900">
                  Permissions ({perms.enabledKeys.size} selected)
                  {perms.loading && <span className="text-sm text-gray-400 ml-2">Loading...</span>}
                </h3>
              </div>

              <div className="border-b border-gray-200 mb-0">
                <nav className="flex -mb-px">
                  <button
                    type="button"
                    onClick={() => setActiveTab('content')}
                    className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'content'
                        ? 'border-ocean-600 text-ocean-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Content Types ({perms.contentEnabledCount}/{perms.contentTypes.reduce((s, ct) => s + ct.availableActions.length, 0)})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('plugins')}
                    className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'plugins'
                        ? 'border-ocean-600 text-ocean-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Plugins & Admin ({perms.pluginEnabledCount}/{perms.allPluginActions.length})
                  </button>
                </nav>
              </div>

              {activeTab === 'content' && (
                <ContentTypesTab
                  loading={perms.loading}
                  contentTypes={perms.contentTypes}
                  isEnabled={perms.isEnabled}
                  togglePerm={perms.togglePerm}
                  toggleAllActionsForCt={perms.toggleAllActionsForCt}
                  toggleField={perms.toggleField}
                  enabledFields={perms.enabledFields}
                  expandedCt={expandedCt}
                  setExpandedCt={setExpandedCt}
                  selectAllContent={perms.selectAllContent}
                  deselectAllContent={perms.deselectAllContent}
                  isSystem={role?.isSystem}
                />
              )}

              {activeTab === 'plugins' && (
                <PluginsTab
                  loading={perms.loading}
                  pluginGroups={perms.pluginGroups}
                  allPluginActions={perms.allPluginActions}
                  isEnabled={perms.isEnabled}
                  togglePerm={perms.togglePerm}
                  selectAllPlugins={perms.selectAllPlugins}
                  deselectAllPlugins={perms.deselectAllPlugins}
                  isSystem={role?.isSystem}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || perms.loading} className="w-full sm:w-auto">
              {isSubmitting ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleForm;
